/**
 * QueryManagerContext - Manages in-flight AI agent queries
 * 
 * This context ensures that queries continue processing even when the user
 * navigates away from the agent chat. Queries are tracked globally and
 * responses are saved to localStorage for retrieval when the user returns.
 */

import React, { createContext, useContext, useRef, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../utils/apiBase';

const QueryManagerContext = createContext(null);

// User-scoped storage keys to prevent data leaking between users
const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.id || 'anon';
  } catch { return 'anon'; }
};
const PENDING_KEY = () => `qm_pending_${getUserId()}`;
const COMPLETED_KEY = () => `qm_completed_${getUserId()}`;

export const QueryManagerProvider = ({ children }) => {
  // Track active queries (not in state to avoid re-renders)
  const activeQueries = useRef(new Map());
  const subscribersRef = useRef(new Map()); // agentId -> Set of callbacks

  // Migrate old non-scoped keys on mount, then clean up stale pending queries
  useEffect(() => {
    try {
      // Migrate legacy keys if present
      const oldPending = localStorage.getItem('autopilot_pending_queries');
      const oldCompleted = localStorage.getItem('autopilot_completed_responses');
      if (oldPending) { localStorage.removeItem('autopilot_pending_queries'); }
      if (oldCompleted) { localStorage.removeItem('autopilot_completed_responses'); }

      const pending = JSON.parse(localStorage.getItem(PENDING_KEY()) || '{}');
      Object.entries(pending).forEach(([queryId, queryData]) => {
        if (Date.now() - queryData.timestamp > 5 * 60 * 1000) {
          delete pending[queryId];
        }
      });
      localStorage.setItem(PENDING_KEY(), JSON.stringify(pending));
    } catch (e) {
      console.warn('Could not load pending queries:', e);
    }
  }, []);

  // Subscribe to query completions for a specific agent
  const subscribe = useCallback((agentId, callback) => {
    if (!subscribersRef.current.has(agentId)) {
      subscribersRef.current.set(agentId, new Set());
    }
    subscribersRef.current.get(agentId).add(callback);
    
    return () => {
      subscribersRef.current.get(agentId)?.delete(callback);
    };
  }, []);

  // Notify subscribers when a query completes
  const notifySubscribers = useCallback((agentId, response) => {
    const subscribers = subscribersRef.current.get(agentId);
    if (subscribers) {
      subscribers.forEach(callback => {
        try {
          callback(response);
        } catch (e) {
          console.error('Error in query subscriber:', e);
        }
      });
    }
  }, []);

  // Start a query - this persists the query and handles the response globally
  const startQuery = useCallback(async ({ 
    agentId, 
    query, 
    isDocumentAgent, 
    conversationHistory,
    productContext,
    onSuccess,
    onError 
  }) => {
    const queryId = `${agentId}_${Date.now()}`;
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    
    const endpoint = isDocumentAgent
      ? `${getApiBaseUrl()}/ai-agents/knowledge-assistants/${agentId}/query`
      : `${getApiBaseUrl()}/ai-agents/data-iq-agents/${agentId}/query`;

    // Save pending query to localStorage (user-scoped)
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_KEY()) || '{}');
      pending[queryId] = {
        agentId,
        query,
        isDocumentAgent,
        timestamp: Date.now(),
        status: 'pending'
      };
      localStorage.setItem(PENDING_KEY(), JSON.stringify(pending));
    } catch (e) {
      console.warn('Could not save pending query:', e);
    }

    // Track this query
    activeQueries.current.set(queryId, { agentId, query, isDocumentAgent });

    console.log('🚀 QueryManager: Starting query', queryId);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(productContext ? { 'x-product-context': productContext } : {}),
        },
        body: JSON.stringify({
          query,
          conversationHistory: conversationHistory?.slice(-10) || []
        })
      });

      const data = await response.json();
      console.log('📥 QueryManager: Response received for', queryId);

      // Remove from pending
      try {
        const pending = JSON.parse(localStorage.getItem(PENDING_KEY()) || '{}');
        delete pending[queryId];
        localStorage.setItem(PENDING_KEY(), JSON.stringify(pending));
      } catch (e) {
        console.warn('Could not clear pending query:', e);
      }

      if (data.success) {
        const aiMessage = {
          role: 'assistant',
          content: data.data.response || data.data.answer,
          sources: data.data.sources,
          accuracy: data.data.accuracy,
          chart: data.data.chart,
          citations: data.data.citations,
          insights: data.data.insights,
          followUpSuggestions: data.data.followUpSuggestions,
          thinkingSteps: data.data.thinkingSteps,
          toolCalls: data.data.toolCalls,
          iterations: data.data.iterations,
          confidence: data.data.confidence,
          verification: data.data.verification,
          queryId
        };

        // Save completed response to localStorage (user-scoped)
        try {
          const completed = JSON.parse(localStorage.getItem(COMPLETED_KEY()) || '{}');
          if (!completed[agentId]) {
            completed[agentId] = [];
          }
          completed[agentId].push({
            userMessage: query,
            aiMessage,
            timestamp: Date.now()
          });
          completed[agentId] = completed[agentId].slice(-10);
          localStorage.setItem(COMPLETED_KEY(), JSON.stringify(completed));
        } catch (e) {
          console.warn('Could not save completed response:', e);
        }

        // Notify subscribers (component might have remounted)
        notifySubscribers(agentId, { success: true, aiMessage, userMessage: query });
        
        // Call success callback if component is still mounted
        if (onSuccess) {
          try {
            onSuccess(aiMessage, query);
          } catch (e) {
            // Component might be unmounted, that's OK
            console.log('📥 QueryManager: Component unmounted, response saved to localStorage');
          }
        }
      } else {
        const error = new Error(data.message || 'Query failed');
        error.code = data.code || null;
        error.status = response.status;
        error.upgradeRequired = Boolean(data.upgradeRequired);
        error.payload = data;
        notifySubscribers(agentId, { success: false, error: error.message });
        if (onError) {
          try {
            onError(error);
          } catch (e) {
            console.log('📥 QueryManager: Component unmounted, error not displayed');
          }
        }
      }
    } catch (error) {
      console.error('QueryManager: Error querying agent:', error);
      
      // Remove from pending
      try {
        const pending = JSON.parse(localStorage.getItem(PENDING_KEY()) || '{}');
        delete pending[queryId];
        localStorage.setItem(PENDING_KEY(), JSON.stringify(pending));
      } catch (e) {
        console.warn('Could not clear pending query:', e);
      }

      notifySubscribers(agentId, { success: false, error: error.message });
      if (onError) {
        try {
          onError(error);
        } catch (e) {
          console.log('📥 QueryManager: Component unmounted, error not displayed');
        }
      }
    } finally {
      activeQueries.current.delete(queryId);
    }

    return queryId;
  }, [notifySubscribers]);

  // Get pending queries for an agent
  const getPendingQueries = useCallback((agentId) => {
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_KEY()) || '{}');
      return Object.entries(pending)
        .filter(([_, data]) => data.agentId === agentId && data.status === 'pending')
        .map(([id, data]) => ({ id, ...data }));
    } catch (e) {
      return [];
    }
  }, []);

  // Get completed responses that haven't been displayed yet
  const getAndClearCompletedResponses = useCallback((agentId) => {
    try {
      const completed = JSON.parse(localStorage.getItem(COMPLETED_KEY()) || '{}');
      const agentResponses = completed[agentId] || [];
      
      // Clear the responses for this agent
      delete completed[agentId];
      localStorage.setItem(COMPLETED_KEY(), JSON.stringify(completed));
      
      return agentResponses;
    } catch (e) {
      return [];
    }
  }, []);

  // Check if there's an active query for an agent
  const hasActiveQuery = useCallback((agentId) => {
    for (const [_, data] of activeQueries.current.entries()) {
      if (data.agentId === agentId) {
        return true;
      }
    }
    return false;
  }, []);

  const value = {
    startQuery,
    subscribe,
    getPendingQueries,
    getAndClearCompletedResponses,
    hasActiveQuery
  };

  return (
    <QueryManagerContext.Provider value={value}>
      {children}
    </QueryManagerContext.Provider>
  );
};

export const useQueryManager = () => {
  const context = useContext(QueryManagerContext);
  if (!context) {
    throw new Error('useQueryManager must be used within a QueryManagerProvider');
  }
  return context;
};

export default QueryManagerContext;




